import * as RadixSelect from '@radix-ui/react-select'
import { Check, ChevronDown } from 'lucide-react'
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react'
import { cn } from '@/lib/cn'
import styles from './Select.module.scss'

export const SelectRoot = RadixSelect.Root
export const SelectValue = RadixSelect.Value
export const SelectGroup = RadixSelect.Group

export interface SelectTriggerProps
  extends ComponentPropsWithoutRef<typeof RadixSelect.Trigger> {
  placeholder?: ReactNode
  error?: boolean
}

export const SelectTrigger = forwardRef<HTMLButtonElement, SelectTriggerProps>(function SelectTrigger(
  { className, error, children, ...rest },
  ref,
) {
  return (
    <RadixSelect.Trigger
      ref={ref}
      className={cn(styles.trigger, error && styles.error, className)}
      {...rest}
    >
      <span className={styles.triggerValue}>{children}</span>
      <RadixSelect.Icon asChild>
        <ChevronDown className={styles.triggerIcon} aria-hidden />
      </RadixSelect.Icon>
    </RadixSelect.Trigger>
  )
})

export const SelectContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof RadixSelect.Content>
>(function SelectContent({ className, children, position = 'popper', sideOffset = 6, ...rest }, ref) {
  return (
    <RadixSelect.Portal>
      <RadixSelect.Content
        ref={ref}
        position={position}
        sideOffset={sideOffset}
        className={cn(styles.content, className)}
        {...rest}
      >
        <RadixSelect.Viewport className={styles.viewport}>{children}</RadixSelect.Viewport>
      </RadixSelect.Content>
    </RadixSelect.Portal>
  )
})

export interface SelectItemProps extends ComponentPropsWithoutRef<typeof RadixSelect.Item> {
  children: ReactNode
}

export const SelectItem = forwardRef<HTMLDivElement, SelectItemProps>(function SelectItem(
  { className, children, ...rest },
  ref,
) {
  return (
    <RadixSelect.Item ref={ref} className={cn(styles.item, className)} {...rest}>
      <span className={styles.itemIndicator}>
        <RadixSelect.ItemIndicator>
          <Check />
        </RadixSelect.ItemIndicator>
      </span>
      <RadixSelect.ItemText>{children}</RadixSelect.ItemText>
    </RadixSelect.Item>
  )
})
